import { Link as ReachLink } from "react-router-dom";
import { usePermition } from "@/hooks/usePermition";
import { useState, useRef, useEffect } from "react";
import { useSideNav } from "@/hooks/useSideNav";
import { ItemMenu } from "./ItemMenu";
import { useBranch } from "@/hooks/useBranch";
import { useAuth } from "@/hooks/useAuth";
import { FiLogOut } from "react-icons/fi";
import expertLogo from "../../../assets/expertosbranco.png";
import logoWhite from "../../../assets/LogoWhite.png";
import { cn } from "@/utils/cn";
import { IoMdHome } from "react-icons/io";
import { Tooltip } from "@chakra-ui/react";
import { forceCacheClear } from "@/utils/forceCacheClear";

export function SideMenu() {
  const [isActive, setIsActive] = useState<number>();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { menus } = usePermition();
  const { open, buttonProps } = useSideNav();
  const { user } = useAuth();
  const { filial } = useBranch();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const userName = user?.split("@")[0] || "";
  const userInitials = user?.substring(0, 2).toUpperCase() || "??";

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      id="sidebar"
      className={cn(
        "z-50 flex flex-col bg-blue-base px-4 py-2 h-screen overflow-x-hidden overflow-y-auto",
        open ? "w-[260px] min-w-[260px]" : "min-w-[70px]",
        !open && "items-center",
      )}
    >
      <button
        {...buttonProps}
        className="flex justify-center items-center bg-blue-base px-3 py-3 w-full"
      >
        <img
          src={open ? expertLogo : logoWhite}
          alt="Expert Logo"
          className={cn(
            "object-contain transition-all duration-300",
            open ? "h-8" : "h-6",
          )}
        />
      </button>
      <Tooltip label={!open ? "Início" : ""}>
        <li className="flex flex-col mb-1 transition-all duration-200 ease-out">
          <ReachLink
            to="/"
            className={`
            group flex items-center text-[13px] outline-none
            transition-all duration-200 ease-out rounded-buttons h-[30px]
            hover:no-underline hover:bg-surface-hover
            focus-visible:shadow-focus focus-visible:outline-none
            ${isActive
                ? "bg-surface-hover text-text-primary shadow-hover"
                : "text-text-inverted hover:text-text-primary"
              }
            ${!open ? "justify-center max-w-[60px]" : "px-2.5"}
          `}
            style={{ width: !open ? "60px" : "auto" }}
            aria-current={isActive ? "page" : undefined}
          >
            <div
              className={`flex flex-1 min-w-0 items-center ${!open && "justify-center"}`}
            >
              <IoMdHome
                className={`
                shrink-0 transition-all flex-grow duration-200 text-md
                ${isActive
                    ? "text-primary-DEFAULT"
                    : "text-text-inverted group-hover:text-text-primary"
                  }
              `}
              />
              <div
                className={`
              transition-all duration-200 ease-out overflow-hidden whitespace-nowrap ml-2.5
              ${open ? "w-auto opacity-100" : "w-0 opacity-0"}
            `}
              >
                <span className="block w-[180px] overflow-hidden text-ellipsis whitespace-nowrap">
                  Início
                </span>
              </div>
            </div>
          </ReachLink>
        </li>
      </Tooltip>
      <ul
        role="navigation"
        aria-label="Menu principal"
        className={cn(
          "flex flex-col flex-grow gap-1 bg-blue-base m-0 overflow-x-hidden overflow-y-auto text-sm list-none",
          !open ? "px-2" : "max-w-[250px]",
        )}
      >
        {menus?.map((item, index) => (
          <ItemMenu
            key={`${item.id || index}-${item.title}`}
            isOpen={index === isActive}
            setIsOpen={() =>
              setIsActive((prev) => (prev === index ? undefined : index))
            }
            {...item}
          />
        ))}
      </ul>

      <div className="relative bg-blue-base border-white/10 border-t">
        <button
          className={cn(
            "flex items-center gap-3 px-2 py-1 pt-3 w-full transition-colors cursor-default",
            !open && "justify-center",
          )}
        >
          <div className="flex flex-shrink-0 justify-center items-center bg-blue-500 rounded-full w-8 h-8 font-bold text-white text-xs">
            {userInitials}
          </div>

          {open && (
            <div className="flex flex-col flex-1 overflow-hidden text-left">
              <span className="font-semibold text-white text-sm truncate uppercase text-wrap">
                {filial?.nomefantasia} - {userName}
              </span>

              <p className="text-gray-400 text-xs truncate">{user}</p>
            </div>
          )}
        </button>

        <button
          onClick={async () => {
            setUserMenuOpen(false);
            await forceCacheClear();
            localStorage.clear();
            window.location.replace(`/signin`);
          }}
          className="flex justify-center items-center gap-2 bg-blue-base hover:bg-surface-hover rounded-[20px] w-full h-[30px] text-white hover:text-text-primary transition-colors"
        >
          <FiLogOut />
          {open && <span>Sair</span>}
        </button>
      </div>
    </div>
  );
}
